/**
 * @fileoverview Input/output schemas for the critique-committee-v2 composite agent.
 *
 * This composite agent runs three specialized critique agents in parallel
 * (prose-flow, sensory-atmosphere, voice-emotion) to provide multi-perspective
 * prose analysis.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  FindingSchema,
  WeaknessSchema,
  IssueSeverityEnum,
} from '@/ai/agents/issue-classification';

// Re-export for use by consumers
export { IssueSeverityEnum, FindingSchema, WeaknessSchema };

/**
 * Result from a single critique agent.
 */
export const CritiqueResultSchema = z.object({
  agentId: z.string().describe('The ID of the critique agent'),
  agentName: z.string().describe('Human-readable name of the critique agent'),
  commentary: z.string().describe('Brief summary of findings and overall assessment'),
  rating: z.number().min(1).max(10).describe('Overall quality rating from 1-10'),
  findings: z.array(FindingSchema).describe('Structured list of issues found'),
  strengths: z.array(z.string()).describe('Things the draft does well'),
  weaknesses: z.array(WeaknessSchema).describe('Systemic issues requiring broader changes'),
  model: z.string().describe('The model used for this critique'),
  durationMs: z.number().optional().describe('Time taken to generate this critique'),
});

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context for critique agents.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.acceptedFindings.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.rejectedFindings.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.acceptedWeaknesses.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.rejectedWeaknesses.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.currentStrengths.schema.shape,
  // Full character profiles for dialogue critique (voice consistency analysis)
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  // Per-genre prose-evaluation guidance — `genre` is auto-injected by
  // buildAgentContext from the project, `genreGuidance` is computed by the
  // caller via `getProseGenreGuidance()`. Forwarded to each child critic.
  ...AGENT_SCHEMA_OBJECTS.genre.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.genreGuidance.schema.shape,
  // POV context — `pov` is the story-level POV setting, `chapterPovCharacter`
  // is the active chapter's narrator. Both are auto-injected by
  // buildAgentContext from the project and forwarded to each child critic so
  // they can render their `{{#if pov}}` / `{{#if chapterPovCharacter}}` blocks.
  // `povCharacter` is the full profile of the active chapter's narrator,
  // consumed by the voice-emotion critic to evaluate voice/emotion authenticity.
  ...AGENT_SCHEMA_OBJECTS.pov.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.povCharacter.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output includes results from all three critique agents.
 */
export const OutputSchema = z.object({
  critiques: z.array(CritiqueResultSchema).describe(
    'Array of critique results from each specialized agent'
  ),
  successCount: z.number().describe(
    'Number of critique agents that completed successfully'
  ),
  failedAgents: z.array(z.string()).optional().describe(
    'IDs of agents that failed, if any'
  ),
  totalDurationMs: z.number().describe(
    'Total time for parallel execution'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type CritiqueResult = z.infer<typeof CritiqueResultSchema>;
